import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthProvider';
import { apiUrl } from '../../lib/api';
import { Plus, TrendingUp, Flame, Clock, ChevronDown, ChevronUp, AlertTriangle, CheckCircle, Bell, Trash2 } from 'lucide-react';

const fontMono = { fontFamily: "'JetBrains Mono', 'Courier New', monospace" };
const fontOrbitron = { fontFamily: "'Orbitron', 'Arial', sans-serif" };

const STAGE_LABELS = {
  intake: 'INTAKE',
  qualified: 'QUALIFIED',
  first_contact: 'FIRST CONTACT',
  follow_up: 'FOLLOW UP',
  proposal: 'PROPOSAL',
  negotiation: 'NEGOTIATION',
  closing: 'CLOSING',
  closed: 'CLOSED',
  lost: 'LOST',
};

const STAGE_COLORS = {
  intake: '#4a5568',
  qualified: '#2b6cb0',
  first_contact: '#2DC896',
  follow_up: '#d69e2e',
  proposal: '#9b59b6',
  negotiation: '#e67e22',
  closing: '#27ae60',
  closed: '#2DC896',
  lost: '#e74c3c',
};

const STAGES = Object.keys(STAGE_LABELS);

const getIntentColor = (score) => {
  if (score >= 75) return '#2DC896';
  if (score >= 50) return '#d69e2e';
  return '#e74c3c';
};

const getIntentBadge = (score, daysSinceContact) => {
  if (score < 25 || (daysSinceContact !== null && daysSinceContact >= 7)) {
    return { text: 'STALE', color: '#6b7280', bg: 'rgba(107,114,128,0.12)' };
  }
  if (score >= 75) return { text: 'HOT', color: '#E84040', bg: 'rgba(232,64,64,0.12)' };
  if (score >= 50) return { text: 'WARM', color: '#EF9F27', bg: 'rgba(239,159,39,0.12)' };
  return { text: 'COLD', color: '#534AB7', bg: 'rgba(83,74,183,0.12)' };
};

const formatCurrency = (val) => {
  if (!val && val !== 0) return '$0';
  return '$' + Number(val)?.toLocaleString();
};

const getDaysSince = (dateStr) => {
  if (!dateStr) return null;
  const diff = Date.now() - new Date(dateStr)?.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const formatDate = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months?.[d?.getMonth()];
  const day = d?.getDate();
  let hours = d?.getHours();
  const mins = String(d?.getMinutes())?.padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `Last updated: ${month} ${day} · ${hours}:${mins} ${ampm}`;
};

const formatActivityTimestamp = (isoStr) => {
  if (!isoStr) return '';
  const d = new Date(isoStr);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const month = months?.[d?.getMonth()];
  const day = d?.getDate();
  let hours = d?.getHours();
  const mins = String(d?.getMinutes())?.padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${month} ${day} · ${hours}:${mins} ${ampm}`;
};

// Simple markdown renderer (no external lib)
const renderMarkdown = (text) => {
  if (!text) return '';
  return text
    ?.replace(/^### (.+)$/gm, '<h3 style="font-size:11px;color:#2DC896;letter-spacing:0.12em;margin:14px 0 6px;font-family:\'Orbitron\',sans-serif;font-weight:700;text-transform:uppercase">$1</h3>')
    ?.replace(/^## (.+)$/gm, '<h2 style="font-size:12px;color:#2DC896;letter-spacing:0.12em;margin:16px 0 8px;font-family:\'Orbitron\',sans-serif;font-weight:700;text-transform:uppercase">$1</h2>')
    ?.replace(/^# (.+)$/gm, '<h1 style="font-size:14px;color:#2DC896;letter-spacing:0.12em;margin:16px 0 8px;font-family:\'Orbitron\',sans-serif;font-weight:700;text-transform:uppercase">$1</h1>')
    ?.replace(/\*\*(.+?)\*\*/g, '<strong style="color:#e8f4ff;font-weight:700">$1</strong>')
    ?.replace(/\*(.+?)\*/g, '<em style="color:#e8f4ffcc">$1</em>')
    ?.replace(/^- (.+)$/gm, '<div style="display:flex;gap:8px;margin:3px 0"><span style="color:#2DC896;flex-shrink:0">▸</span><span>$1</span></div>')
    ?.replace(/\n\n/g, '<br/><br/>')
    ?.replace(/\n/g, '<br/>');
};

const getStageNextAction = (stage) => {
  const map = {
    intake: 'Build lead profile and ICP match',
    qualified: 'Prepare first outreach angle',
    first_contact: 'Send first outreach message',
    follow_up: 'Send follow-up message',
    proposal: 'Prepare or send proposal',
    negotiation: 'Handle objections and confirm terms',
    closing: 'Confirm payment and mark deal won',
    closed: 'Start delivery handoff',
    lost: 'Archive or revive later',
  };
  return map[stage] || 'Review and take next step';
};

const AddLeadModal = ({ onClose, onAdd }) => {
  const [form, setForm] = useState({ name: '', company: '', deal_value: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!form?.name?.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    const err = await onAdd(form);
    setSaving(false);
    if (err) { setError(err); } else { onClose(); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#0d1117', border: '1px solid rgba(45,200,150,0.25)', borderRadius: 10, padding: 28, width: 360, maxWidth: '90vw' }}>
        <div style={{ ...fontOrbitron, fontSize: 11, fontWeight: 700, color: '#2DC896', letterSpacing: '0.15em', marginBottom: 20 }}>ADD NEW LEAD</div>
        <form onSubmit={handleSubmit}>
          {[
            { key: 'name', label: 'NAME *', placeholder: 'Contact name' },
            { key: 'company', label: 'COMPANY', placeholder: 'Company name' },
            { key: 'deal_value', label: 'DEAL VALUE ($)', placeholder: '0' },
          ]?.map(({ key, label, placeholder }) => (
            <div key={key} style={{ marginBottom: 14 }}>
              <div style={{ ...fontMono, fontSize: 9, color: '#2DC89680', letterSpacing: '0.12em', marginBottom: 5 }}>{label}</div>
              <input
                type={key === 'deal_value' ? 'number' : 'text'}
                placeholder={placeholder}
                value={form?.[key]}
                onChange={e => setForm(f => ({ ...f, [key]: e?.target?.value }))}
                style={{ width: '100%', background: 'rgba(45,200,150,0.04)', border: '1px solid rgba(45,200,150,0.15)', borderRadius: 5, padding: '8px 12px', ...fontMono, fontSize: 12, color: '#e8f4ff', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          ))}
          {error && <div style={{ ...fontMono, fontSize: 11, color: '#e74c3c', marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
            <button
              type="button"
              onClick={onClose}
              style={{ flex: 1, padding: '9px 0', background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 5, ...fontMono, fontSize: 11, color: '#e8f4ff60', cursor: 'pointer', letterSpacing: '0.08em' }}
            >
              CANCEL
            </button>
            <button
              type="submit"
              disabled={saving}
              style={{ flex: 1, padding: '9px 0', background: saving ? 'rgba(45,200,150,0.1)' : 'rgba(45,200,150,0.15)', border: '1px solid rgba(45,200,150,0.4)', borderRadius: 5, ...fontMono, fontSize: 11, color: '#2DC896', cursor: saving ? 'not-allowed' : 'pointer', letterSpacing: '0.08em' }}
            >
              {saving ? 'SAVING...' : 'ADD LEAD'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const ImportCsvModal = ({ onClose, onImportDone }) => {
  const [file, setFile] = useState(null);
  const [status, setStatus] = useState('');
  const [importing, setImporting] = useState(false);
  const [isError, setIsError] = useState(false);

  const handleImport = async () => {
    if (!file) { setIsError(true); setStatus('Please select a CSV file.'); return; }
    setImporting(true);
    setStatus('');
    setIsError(false);
    try {
      const text = await file?.text();
      const lines = text?.split('\n')?.map(l => l?.trim())?.filter(l => l?.length > 0);
      if (lines?.length < 2) { setIsError(true); setStatus('CSV is empty or has no data rows.'); setImporting(false); return; }
      const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.toLowerCase()?.replace(/^"|"$/g, ''));
      if (!headers?.includes('name')) { setIsError(true); setStatus('CSV must include a "name" column.'); setImporting(false); return; }
      const leads = lines?.slice(1)?.map(line => {
        const values = line?.split(',')?.map(v => v?.trim()?.replace(/^"|"$/g, ''));
        const obj = {};
        headers?.forEach((h, i) => { obj[h] = values?.[i] || ''; });
        return obj;
      })?.filter(l => l?.name);

      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error('Not authenticated.');
      const res = await fetch(apiUrl('/api/leads/import'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ leads }),
      });
      if (!res?.ok) {
        const errText = await res?.text();
        setIsError(true);
        setStatus(`Import failed: ${errText || res?.statusText}`);
      } else {
        setIsError(false);
        setStatus(`${leads?.length} lead${leads?.length !== 1 ? 's' : ''} imported.`);
        onImportDone && onImportDone();
      }
    } catch (err) {
      setIsError(true);
      setStatus(`Error: ${err?.message || 'Failed to import.'}`);
    }
    setImporting(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#0d1117', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 10, padding: '28px 28px 24px', width: 420, maxWidth: '90vw' }}>
        <div style={{ ...fontOrbitron, fontSize: 11, color: '#2DC896', letterSpacing: '0.15em', marginBottom: 20 }}>IMPORT CSV</div>
        <p style={{ ...fontMono, fontSize: 11, color: '#e8f4ff60', lineHeight: 1.7, marginBottom: 18 }}>
          CSV must include columns: <span style={{ color: '#e8f4ffaa' }}>name, company, deal_value</span>.<br />
          Optional: <span style={{ color: '#e8f4ff70' }}>email, phone, source, notes</span>.
        </p>
        <input
          type="file"
          accept=".csv"
          onChange={e => { setFile(e?.target?.files?.[0] || null); setStatus(''); setIsError(false); }}
          style={{ display: 'block', width: '100%', marginBottom: 16, ...fontMono, fontSize: 11, color: '#e8f4ffaa', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 5, padding: '8px 10px', cursor: 'pointer' }}
        />
        {status && (
          <div style={{ ...fontMono, fontSize: 11, color: isError ? '#ff6b6b' : '#2DC896', marginBottom: 14, padding: '8px 10px', background: isError ? 'rgba(255,107,107,0.08)' : 'rgba(45,200,150,0.08)', borderRadius: 5, border: `1px solid ${isError ? 'rgba(255,107,107,0.2)' : 'rgba(45,200,150,0.2)'}` }}>
            {status}
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
          <button
            onClick={onClose}
            style={{ flex: 1, padding: '9px 0', background: 'none', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 5, ...fontMono, fontSize: 11, color: '#e8f4ff60', cursor: 'pointer', letterSpacing: '0.08em' }}
          >
            CANCEL
          </button>
          <button
            onClick={handleImport}
            disabled={importing}
            style={{ flex: 1, padding: '9px 0', background: importing ? 'rgba(45,200,150,0.1)' : 'rgba(45,200,150,0.15)', border: '1px solid rgba(45,200,150,0.4)', borderRadius: 5, ...fontMono, fontSize: 11, color: '#2DC896', cursor: importing ? 'not-allowed' : 'pointer', letterSpacing: '0.08em' }}
          >
            {importing ? 'IMPORTING...' : 'IMPORT'}
          </button>
        </div>
      </div>
    </div>
  );
};

const LeadRow = ({ lead, onStageChange, onDelete }) => {
  const [expanded, setExpanded] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [approveStatus, setApproveStatus] = useState('');
  const [deliverable, setDeliverable] = useState(null);
  const [activityExpanded, setActivityExpanded] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const days = getDaysSince(lead?.last_contact_date);

  const handleStageChange = async (newStage) => {
    if (newStage === lead?.stage) return;
    setUpdating(true);
    await onStageChange(lead?.id, newStage);
    setUpdating(false);
  };

  const handleApprove = async () => {
    setApproving(true);
    setApproveStatus('');
    setDeliverable(null);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData?.session?.access_token;
      if (!accessToken) throw new Error('Not authenticated.');
      const res = await fetch(apiUrl('/api/sales-crew/approve'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ lead_id: lead?.id, action: lead?.next_action, bot: lead?.next_action_bot }),
      });
      if (!res?.ok) {
        const errData = await res?.json()?.catch(() => ({}));
        setApproveStatus(errData?.error || errData?.message || `Error ${res?.status}`);
      } else {
        const data = await res?.json()?.catch(() => ({}));
        setDeliverable(data?.deliverable || null);
        setApproveStatus('success');
      }
    } catch (err) {
      setApproveStatus(err?.message || 'Request failed.');
    } finally {
      setApproving(false);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    const confirmed = window.confirm(`Delete lead "${lead?.name}"? This cannot be undone.`);
    if (!confirmed) return;
    setDeleting(true);
    await onDelete(lead?.id);
    setDeleting(false);
  };

  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{ border: '1px solid rgba(45,200,150,0.08)', borderRadius: 7, background: 'rgba(45,200,150,0.02)', overflow: 'hidden', transition: 'border-color 0.2s' }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(45,200,150,0.18)'; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(45,200,150,0.08)'; }}
      >
        <div
          style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer' }}
          onClick={() => setExpanded(v => !v)}
        >
          <div style={{ width: 38, height: 38, borderRadius: '50%', border: `2px solid ${getIntentColor(lead?.intent_score)}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <span style={{ ...fontOrbitron, fontSize: 10, fontWeight: 700, color: getIntentColor(lead?.intent_score) }}>{lead?.intent_score ?? 50}</span>
          </div>

          {lead?.stage === 'closed' ? (
            <span style={{ fontFamily: "'Share Tech Mono', monospace", fontSize: 10, padding: '2px 8px', borderRadius: 0, textTransform: 'uppercase', letterSpacing: '1px', display: 'inline-block', color: '#2DC896', background: 'rgba(45,200,150,0.12)', flexShrink: 0 }}>
              DEAL WON
            </span>
          ) : (() => {
            const badge = getIntentBadge(lead?.intent_score ?? 50, days);
            return (
              <span style={{
                fontFamily: "'Share Tech Mono', monospace",
                fontSize: 10,
                padding: '2px 8px',
                borderRadius: 0,
                textTransform: 'uppercase',
                letterSpacing: '1px',
                display: 'inline-block',
                color: badge?.color,
                background: badge?.bg,
                flexShrink: 0,
              }}>
                {badge?.text}
              </span>
            );
          })()}

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...fontMono, fontSize: 13, color: '#e8f4ff', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {lead?.name}
            </div>
            {lead?.company && <div style={{ ...fontMono, fontSize: 10, color: '#e8f4ff50', marginTop: 2 }}>{lead?.company}</div>}
          </div>

          <div style={{ ...fontMono, fontSize: 12, color: '#2DC896', flexShrink: 0 }}>{formatCurrency(lead?.deal_value)}</div>

          <div style={{ padding: '3px 8px', borderRadius: 3, background: `${STAGE_COLORS?.[lead?.stage] || '#4a5568'}20`, border: `1px solid ${STAGE_COLORS?.[lead?.stage] || '#4a5568'}50`, ...fontMono, fontSize: 9, color: STAGE_COLORS?.[lead?.stage] || '#e8f4ff', letterSpacing: '0.08em', flexShrink: 0 }}>
            {STAGE_LABELS?.[lead?.stage] || lead?.stage?.toUpperCase()}
          </div>

          {days !== null && (
            <div style={{ ...fontMono, fontSize: 10, color: days > 7 ? '#e74c3c80' : '#e8f4ff40', flexShrink: 0 }}>
              {days}d
            </div>
          )}

          <button
            onClick={handleDelete}
            disabled={deleting}
            title="Delete lead"
            style={{
              background: 'transparent',
              border: '1px solid rgba(231,76,60,0.2)',
              borderRadius: 4,
              padding: '6px',
              cursor: deleting ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => {
              if (!deleting) {
                e.currentTarget.style.background = 'rgba(231,76,60,0.08)';
                e.currentTarget.style.borderColor = 'rgba(231,76,60,0.45)';
              }
            }}
            onMouseLeave={e => {
              if (!deleting) {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.borderColor = 'rgba(231,76,60,0.2)';
              }
            }}
          >
            <Trash2 style={{ width: 13, height: 13, color: deleting ? '#e8f4ff40' : '#e74c3c' }} />
          </button>

          {expanded
            ? <ChevronUp style={{ width: 14, height: 14, color: '#2DC89660', flexShrink: 0 }} />
            : <ChevronDown style={{ width: 14, height: 14, color: '#2DC89660', flexShrink: 0 }} />}
        </div>

        {expanded && (
          <div style={{ padding: '0 16px 14px', borderTop: '1px solid rgba(45,200,150,0.06)' }}>
            <div style={{ ...fontMono, fontSize: 9, color: '#2DC89670', letterSpacing: '0.12em', marginTop: 12, marginBottom: 8 }}>UPDATE STAGE</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {STAGES?.map(s => (
                <button
                  key={s}
                  disabled={updating}
                  onClick={() => handleStageChange(s)}
                  style={{ padding: '4px 10px', borderRadius: 3, background: lead?.stage === s ? `${STAGE_COLORS?.[s]}25` : 'transparent', border: `1px solid ${lead?.stage === s ? STAGE_COLORS?.[s] : 'rgba(255,255,255,0.08)'}`, ...fontMono, fontSize: 9, color: lead?.stage === s ? STAGE_COLORS?.[s] : '#e8f4ff50', cursor: updating ? 'not-allowed' : 'pointer', letterSpacing: '0.06em', transition: 'all 0.15s' }}
                >
                  {STAGE_LABELS?.[s]}
                </button>
              ))}
            </div>

            {lead?.stage === 'closed' ? (
              <div style={{ marginTop: 12, background: 'rgba(45,200,150,0.05)', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 6, padding: '12px 16px' }}>
                <div style={{ ...fontOrbitron, fontSize: 10, color: '#2DC896', letterSpacing: '0.12em', fontWeight: 700, marginBottom: 4 }}>DEAL WON</div>
                <div style={{ ...fontMono, fontSize: 11, color: '#e8f4ffaa', marginBottom: 2 }}>{lead?.deal_value ? '$' + Number(lead.deal_value).toLocaleString() + ' secured' : 'Deal secured'}</div>
                <div style={{ ...fontMono, fontSize: 10, color: '#2DC89680' }}>Ready for Delivery</div>
              </div>
            ) : null}
            {(lead?.stage === 'closed' || lead?.lifecycle_state === 'closed') && (
              <div style={{ marginTop: 10 }}>
                <button
                  onClick={() => window.location.href = '/delivery-command-center'}
                  style={{ width: '100%', padding: '12px 0', background: 'rgba(45,200,150,0.12)', border: '1px solid rgba(45,200,150,0.5)', borderRadius: 4, fontFamily: "'Orbitron', sans-serif", fontSize: 11, fontWeight: 700, color: '#2DC896', letterSpacing: '0.12em', cursor: 'pointer', textTransform: 'uppercase', transition: 'all 0.15s' }}
                  onMouseEnter={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.25)'; e.currentTarget.style.borderColor = '#2DC896'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.12)'; e.currentTarget.style.borderColor = 'rgba(45,200,150,0.5)'; }}
                >
                  START DELIVERY
                </button>
              </div>
            )}

            {lead?.notes && (
              <div style={{ marginTop: 10, ...fontMono, fontSize: 11, color: '#e8f4ff60', lineHeight: 1.6 }}>{lead?.notes}</div>
            )}
          </div>
        )}
      </div>

      {deliverable && (
        <div style={{ marginTop: 6 }}>
          <div style={{ ...fontMono, fontSize: 9, color: '#2DC896', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 5, textTransform: 'uppercase', paddingLeft: 4 }}>MISSION OUTPUT</div>
          <div style={{ position: 'relative', background: '#0a0e14', borderLeft: '3px solid #2DC896', borderTop: '1px solid rgba(45,200,150,0.15)', borderRight: '1px solid rgba(45,200,150,0.15)', borderBottom: '1px solid rgba(45,200,150,0.15)', borderRadius: '0 4px 4px 0', padding: '10px 14px 12px 14px' }}>
            <button
              onClick={() => { navigator?.clipboard?.writeText(deliverable); }}
              style={{ position: 'absolute', top: 8, right: 10, padding: '3px 10px', background: 'rgba(45,200,150,0.1)', border: '1px solid rgba(45,200,150,0.35)', borderRadius: 3, ...fontMono, fontSize: 9, color: '#2DC896', cursor: 'pointer', letterSpacing: '0.08em', transition: 'background 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.22)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.1)'; }}
            >
              COPY
            </button>
            <div style={{ maxHeight: 300, overflowY: 'auto', paddingRight: 4, marginTop: 4 }}>
              <pre style={{ ...fontMono, fontSize: 11, color: '#b8d4c8', lineHeight: 1.65, margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{deliverable}</pre>
            </div>
          </div>
        </div>
      )}

      <div style={{ marginTop: 6, paddingLeft: 4 }}>
        <button
          onClick={() => setActivityExpanded(v => !v)}
          style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', ...fontMono, fontSize: 9, color: '#2DC89680', letterSpacing: '0.1em', textDecoration: 'none', transition: 'color 0.15s' }}
          onMouseEnter={e => { e.currentTarget.style.color = '#2DC896'; }}
          onMouseLeave={e => { e.currentTarget.style.color = '#2DC89680'; }}
        >
          {activityExpanded ? '▲ HIDE HISTORY' : '▼ VIEW HISTORY'}
        </button>

        {activityExpanded && (() => {
          const log = Array.isArray(lead?.activity_log) ? [...lead?.activity_log]?.reverse() : [];
          return (
            <div style={{ marginTop: 8, background: 'rgba(45,200,150,0.02)', border: '1px solid rgba(45,200,150,0.08)', borderRadius: 6, padding: '10px 14px' }}>
              <div style={{ ...fontMono, fontSize: 9, color: '#2DC896', letterSpacing: '0.15em', fontWeight: 700, marginBottom: 10, textTransform: 'uppercase' }}>ACTIVITY LOG</div>
              {log?.length === 0 ? (
                <div style={{ ...fontMono, fontSize: 11, color: '#e8f4ff30' }}>No activity yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {log?.map((entry, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#2DC896', flexShrink: 0, marginTop: 3 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ ...fontMono, fontSize: 9, color: '#e8f4ff30', letterSpacing: '0.06em' }}>{formatActivityTimestamp(entry?.timestamp || entry?.created_at)}</span>
                          {entry?.event_type && (
                            <span style={{ ...fontMono, fontSize: 9, color: '#2DC896', letterSpacing: '0.12em', fontWeight: 700, textTransform: 'uppercase' }}>{entry?.event_type}</span>
                          )}
                        </div>
                        {entry?.action && (
                          <div style={{ ...fontMono, fontSize: 11, color: '#b8d4c8', marginTop: 2, lineHeight: 1.5 }}>{entry?.action}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

const LeadCommandCenter = () => {
  const { user, loading: authLoading } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [commandBrief, setCommandBrief] = useState(null);
  const [briefExpanded, setBriefExpanded] = useState(false);
  const [briefLoading, setBriefLoading] = useState(true);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    setError('');

    const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
    if (sessionError) {
      console.error('[LeadCommandCenter] Session error:', sessionError);
      setError('Session error: ' + sessionError?.message);
      setLoading(false);
      return;
    }

    const sessionUser = sessionData?.session?.user;
    if (!sessionUser?.id) {
      console.error('[LeadCommandCenter] No active session found. User is not authenticated.');
      setError('No active session. Please log in again.');
      setLoading(false);
      return;
    }

    try {
      console.log('[LeadCommandCenter] Fetching leads for user:', sessionUser?.id);
      const { data, error: fetchError } = await supabase?.from('leads')?.select('*')?.eq('user_id', sessionUser?.id)?.order('intent_score', { ascending: false });

      if (fetchError) {
        console.error('[LeadCommandCenter] Supabase fetch error:', fetchError);
        setError('Failed to load leads: ' + fetchError?.message);
      } else {
        console.log('[LeadCommandCenter] Leads fetched:', data?.length ?? 0);
        setLeads((data || []).filter(l => l?.stage !== 'archived'));
      }
    } catch (err) {
      console.error('[LeadCommandCenter] Unexpected error:', err);
      setError(err?.message || 'Failed to load leads.');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCommandBrief = useCallback(async () => {
    setBriefLoading(true);
    try {
      const { data, error: briefError } = await supabase
        ?.from('command_briefs')
        ?.select('id, content, generated_at')
        ?.eq('id', `sales_command_brief:${user?.id}`)
        ?.maybeSingle();
      if (briefError) {
        console.error('[LeadCommandCenter] Command brief fetch error:', briefError);
      } else {
        setCommandBrief(data || null);
      }
    } catch (err) {
      console.error('[LeadCommandCenter] Command brief unexpected error:', err);
    } finally {
      setBriefLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (authLoading) return;
    fetchLeads();
    if (user?.id) fetchCommandBrief();
  }, [authLoading, fetchLeads, fetchCommandBrief]);

  const addLead = async (form) => {
    const { data: sessionData, error: sessionError } = await supabase?.auth?.getSession();
    if (sessionError) {
      console.error('[LeadCommandCenter] addLead session error:', sessionError);
      return 'Session error: ' + sessionError?.message;
    }
    const sessionUser = sessionData?.session?.user;
    if (!sessionUser?.id) return 'Not authenticated.';
    try {
      const { error: insertError } = await supabase?.from('leads')?.insert({
        name: form?.name?.trim(),
        company: form?.company?.trim() || null,
        deal_value: form?.deal_value ? Number(form?.deal_value) : 0,
        stage: 'intake',
        intent_score: 50,
        source: 'manual',
        user_id: sessionUser?.id,
        last_contact_date: new Date()?.toISOString(),
      });
      if (insertError) {
        console.error('[LeadCommandCenter] Insert error:', insertError);
        return insertError?.message;
      }
      await fetchLeads();
      return null;
    } catch (err) {
      console.error('[LeadCommandCenter] addLead unexpected error:', err);
      return err?.message || 'Failed to add lead.';
    }
  };

  const updateLeadStage = async (id, newStage) => {
    try {
      if (!user?.id) throw new Error('Not authenticated.');
      const { error: updateError } = await supabase?.from('leads')?.update({ stage: newStage, updated_at: new Date()?.toISOString() })?.eq('id', id)?.eq('user_id', user.id);
      if (updateError) {
        console.error('[LeadCommandCenter] Update stage error:', updateError);
        setError(updateError?.message);
        return;
      }
      setLeads(prev => prev?.map(l => l?.id === id ? { ...l, stage: newStage } : l));
    } catch (err) {
      console.error('[LeadCommandCenter] updateLeadStage unexpected error:', err);
      setError(err?.message || 'Failed to update stage.');
    }
  };

  const deleteLead = async (id) => {
    try {
      if (!user?.id) throw new Error('Not authenticated.');
      const { error: archiveError } = await supabase?.from('leads')?.update({ stage: 'archived', updated_at: new Date().toISOString() })?.eq('id', id)?.eq('user_id', user.id);
      if (archiveError) {
        console.error('[LeadCommandCenter] Archive error:', archiveError);
        return;
      }
      setLeads(prev => prev.filter(l => l?.id !== id));
    } catch (err) {
      console.error('[LeadCommandCenter] deleteLead unexpected error:', err);
    }
  };

  const activeLeads = leads?.filter(l => l?.stage !== 'closed' && l?.stage !== 'lost');
  const closedRevenue = leads?.filter(l => l?.stage === 'closed')?.reduce((sum, l) => sum + (Number(l?.deal_value) || 0), 0);
  const pipelineValue = activeLeads?.reduce((sum, l) => sum + (Number(l?.deal_value) || 0), 0);
  const hotLeadsCount = leads?.filter(l => (l?.intent_score ?? 0) >= 75)?.length;
  const atRiskValue = leads
    ?.filter(l => l?.intent_decay === 'fast' || (l?.days_since_contact ?? getDaysSince(l?.last_contact_date) ?? 0) >= 7)
    ?.reduce((sum, l) => sum + (Number(l?.deal_value) || 0), 0);
  const pendingApprovalsCount = leads?.filter(l => l?.next_action_status === 'pending_approval')?.length;
  const actionsExecutedCount = leads?.filter(l => l?.next_action_status === 'executed')?.length;
  const staleCount = leads?.filter(l => {
    const dsc = l?.days_since_contact ?? getDaysSince(l?.last_contact_date);
    return dsc !== null && dsc >= 7;
  })?.length;

  const metrics = [
    { label: 'PIPELINE VALUE', value: formatCurrency(pipelineValue), icon: TrendingUp, color: '#2DC896' },
    { label: 'HOT LEADS', value: hotLeadsCount, icon: Flame, color: '#e67e22' },
    { label: 'AT-RISK VALUE', value: formatCurrency(atRiskValue), icon: AlertTriangle, color: '#e74c3c' },
    { label: 'PENDING APPROVALS', value: pendingApprovalsCount, icon: Bell, color: '#d69e2e' },
    { label: 'ACTIONS EXECUTED', value: actionsExecutedCount, icon: CheckCircle, color: '#2DC896' },
    { label: 'CLOSED REVENUE', value: formatCurrency(closedRevenue), icon: CheckCircle, color: '#534AB7' },
  ];

  return (
    <div style={{ minHeight: '100vh', background: '#05060f', color: '#e8f4ff', padding: '40px 24px 80px' }}>
      <div style={{ maxWidth: 860, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 32, flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ ...fontOrbitron, fontSize: 9, color: '#2DC89660', letterSpacing: '0.2em', marginBottom: 6 }}>RECALLCORE CRM</div>
            <h1 style={{ ...fontOrbitron, fontSize: 22, fontWeight: 700, color: '#e8f4ff', margin: 0, letterSpacing: '0.05em' }}>LEAD COMMAND CENTER</h1>
            <div style={{ ...fontMono, fontSize: 11, color: '#e8f4ff40', marginTop: 6 }}>
              {loading ? 'Loading...' : `${leads?.length} lead${leads?.length !== 1 ? 's' : ''} tracked`}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => setShowImportModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: 'rgba(45,200,150,0.06)', border: '1px solid rgba(45,200,150,0.25)', borderRadius: 6, ...fontOrbitron, fontSize: 10, fontWeight: 700, color: '#2DC89699', cursor: 'pointer', letterSpacing: '0.1em', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.12)'; e.currentTarget.style.color = '#2DC896'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.06)'; e.currentTarget.style.color = '#2DC89699'; }}
            >
              IMPORT CSV
            </button>

            <button
              onClick={() => setShowAddModal(true)}
              style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '10px 18px', background: 'rgba(45,200,150,0.1)', border: '1px solid rgba(45,200,150,0.35)', borderRadius: 6, ...fontOrbitron, fontSize: 10, fontWeight: 700, color: '#2DC896', cursor: 'pointer', letterSpacing: '0.1em', transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.18)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.1)'; }}
            >
              <Plus style={{ width: 14, height: 14 }} /> ADD LEAD
            </button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 24 }}>
          {metrics?.map(({ label, value, icon: MetricIcon, color }) => (
            <div key={label} style={{ background: 'rgba(45,200,150,0.03)', border: '1px solid rgba(45,200,150,0.08)', borderRadius: 8, padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <MetricIcon style={{ width: 14, height: 14, color }} />
                <span style={{ ...fontMono, fontSize: 9, color: '#e8f4ff40', letterSpacing: '0.12em' }}>{label}</span>
              </div>
              <div style={{ ...fontOrbitron, fontSize: 20, fontWeight: 700, color }}>{loading ? '—' : value}</div>
            </div>
          ))}
        </div>

        {/* Mission Output Notification */}
        <div style={{ marginBottom: 16, background: 'rgba(45,200,150,0.04)', border: '1px solid rgba(45,200,150,0.2)', borderLeft: '3px solid #2DC896', borderRadius: '0 6px 6px 0', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...fontOrbitron, fontSize: 10, fontWeight: 700, color: '#2DC896', letterSpacing: '0.12em', marginBottom: 4 }}>NEW MISSION OUTPUT DETECTED</div>
            <div style={{ ...fontMono, fontSize: 11, color: 'rgba(232,244,255,0.6)', lineHeight: 1.5 }}>Your last mission created sales-ready material. Do you want to turn it into a lead?</div>
          </div>
          <button
            onClick={() => setShowAddModal(true)}
            style={{ background: 'rgba(45,200,150,0.12)', border: '1px solid rgba(45,200,150,0.4)', borderRadius: 4, padding: '7px 16px', ...fontOrbitron, fontSize: 9, fontWeight: 700, color: '#2DC896', cursor: 'pointer', letterSpacing: '0.1em', textTransform: 'uppercase', flexShrink: 0, whiteSpace: 'nowrap' }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.22)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'rgba(45,200,150,0.12)'; }}
          >
            ADD AS LEAD
          </button>
        </div>
        <div style={{ marginBottom: 28 }}>
          <div style={{ ...fontMono, fontSize: 9, color: '#2DC89660', letterSpacing: '0.2em', marginBottom: 10, textTransform: 'uppercase' }}>DAILY MOVEMENT</div>
          <div style={{ background: 'rgba(45,200,150,0.02)', border: '1px solid rgba(45,200,150,0.1)', borderRadius: 8, overflow: 'hidden' }}>
            <div
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', cursor: 'pointer' }}
              onClick={() => setBriefExpanded(v => !v)}
            >
              <div>
                <div style={{ ...fontMono, fontSize: 9, color: '#2DC896', letterSpacing: '0.15em', fontWeight: 700, textTransform: 'uppercase' }}>SALES COMMAND BRIEF</div>
                {commandBrief?.generated_at && (
                  <div style={{ ...fontMono, fontSize: 9, color: '#e8f4ff30', marginTop: 3 }}>{formatDate(commandBrief?.generated_at)}</div>
                )}
                {!commandBrief && !briefLoading && (
                  <div style={{ ...fontMono, fontSize: 9, color: '#e8f4ff30', marginTop: 3 }}>No brief available</div>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ ...fontMono, fontSize: 9, color: '#2DC896', letterSpacing: '0.1em', border: '1px solid rgba(45,200,150,0.3)', borderRadius: 3, padding: '3px 10px', background: 'rgba(45,200,150,0.06)' }}>
                  {briefExpanded ? 'HIDE BRIEF' : 'VIEW BRIEF'}
                </span>
                {briefExpanded
                  ? <ChevronUp style={{ width: 14, height: 14, color: '#2DC89660' }} />
                  : <ChevronDown style={{ width: 14, height: 14, color: '#2DC89660' }} />}
              </div>
            </div>

            {briefExpanded && (
              <div style={{ borderTop: '1px solid rgba(45,200,150,0.08)', padding: '16px 18px', background: '#0a0e14' }}>
                {briefLoading ? (
                  <div style={{ ...fontMono, fontSize: 11, color: '#e8f4ff40' }}>Loading brief...</div>
                ) : commandBrief?.content ? (
                  <div
                    style={{ ...fontMono, fontSize: 12, color: '#b8d4c8', lineHeight: 1.75, maxHeight: 400, overflowY: 'auto' }}
                    dangerouslySetInnerHTML={{ __html: renderMarkdown(commandBrief?.content) }}
                  />
                ) : (
                  <div style={{ ...fontMono, fontSize: 11, color: '#e8f4ff30' }}>No brief yet. Run a Sales Crew cycle to generate your first brief.</div>
                )}
              </div>
            )}
          </div>
        </div>

        {error && (
          <div style={{ ...fontMono, fontSize: 11, color: '#e74c3c', background: 'rgba(231,76,60,0.08)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 6, padding: '10px 14px', marginBottom: 20 }}>
            {error}
          </div>
        )}

        <div>
          <div style={{ ...fontMono, fontSize: 9, color: '#2DC89660', letterSpacing: '0.15em', marginBottom: 14 }}>
            LEADS — SORTED BY INTENT SCORE
          </div>

          {loading ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[1, 2, 3]?.map(i => (
                <div key={i} style={{ height: 62, borderRadius: 7, background: 'rgba(45,200,150,0.03)', border: '1px solid rgba(45,200,150,0.06)', animation: 'pulse 1.5s ease-in-out infinite' }} />
              ))}
            </div>
          ) : leads?.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px', border: '1px dashed rgba(45,200,150,0.12)', borderRadius: 10 }}>
              <div style={{ ...fontOrbitron, fontSize: 13, color: '#e8f4ff30', marginBottom: 10 }}>NO LEADS YET</div>
              <div style={{ ...fontMono, fontSize: 12, color: '#e8f4ff40' }}>No leads yet. Add your first lead.</div>
              <button
                onClick={() => setShowAddModal(true)}
                style={{ marginTop: 20, display: 'inline-flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'rgba(45,200,150,0.08)', border: '1px solid rgba(45,200,150,0.25)', borderRadius: 6, ...fontMono, fontSize: 11, color: '#2DC896', cursor: 'pointer', letterSpacing: '0.08em' }}
              >
                <Plus style={{ width: 13, height: 13 }} /> ADD YOUR FIRST LEAD
              </button>
            </div>
          ) : (
            leads?.map(lead => (
              <LeadRow
                key={lead?.id}
                lead={lead}
                onStageChange={updateLeadStage}
                onDelete={deleteLead}
              />
            ))
          )}
        </div>
      </div>

      {showAddModal && (
        <AddLeadModal onClose={() => setShowAddModal(false)} onAdd={addLead} />
      )}

      {showImportModal && (
        <ImportCsvModal onClose={() => setShowImportModal(false)} onImportDone={() => { fetchLeads(); }} />
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default LeadCommandCenter;
